// ─────────────────────────────────────────
// GroupCompete.js — live scoring competitions
// ─────────────────────────────────────────
// Lets group members create competitions with:
//   - Format: 1v1, 2v2, Teams, 4-some, Group
//   - Scoring: Round scores OR custom (0–9,999)
// Scores update live for all members via Supabase real-time.
//
// Score entry: two modes per player —
//   1. Hole-by-hole scorecard (18 boxes, front/back 9, running total)
//   2. Final score total (quick entry)

import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import AvatarImg from './AvatarImg'
import ScorecardOverlay from './ScorecardOverlay'

const SCORING_TYPES = [
  { key: 'rounds', label: '⛳ Golf Score', hint: 'low wins' },
  { key: 'custom', label: '🎯 Custom Points', hint: 'high wins' }
]

const SCORING_FORMATS = [
  { key: 'best_ball', label: 'Best Ball', desc: 'Everyone plays their own ball. Lowest score on each hole counts for the team.' },
  { key: 'best_ball_skins', label: 'Best Ball Skins', desc: 'Each hole is worth a skin. Best ball per team, lowest net score wins it.' },
  { key: 'scramble', label: 'Scramble', desc: 'Everyone hits, pick the best shot, all play from there. Repeat until holed.' },
  { key: 'shamble', label: 'Shamble', desc: "Everyone plays from the best drive, then plays their own ball in. Best score on each hole counts." },
  { key: 'alternate_shot', label: 'Alternate Shot', desc: 'Partners take turns hitting the same ball. One tees off on odds, the other on evens.' },
  { key: 'greensomes', label: 'Greensomes', desc: 'Both partners tee off, pick the better drive, then alternate shots from there.' },
  { key: 'chapman', label: 'Chapman / Pinehurst', desc: 'Both tee off, switch balls for the second shot, then pick one ball and alternate to finish.' },
  { key: 'las_vegas', label: 'Las Vegas', desc: 'Two scores combined into a 2-digit number (low digit first). A 4 and a 6 = 46. Lowest total wins.' },
  { key: 'triple_6_mixer', label: 'Triple 6 Mixer', desc: 'Par 3s: Best Ball. Par 4s: Vegas (2-digit combo). Par 5s: Combined (add both). Lowest total wins.' },
  { key: 'double_dice', label: 'Double Dice', desc: 'Front 9: Combined (add both scores). Back 9: Vegas (2-digit combo, low digit first). Lowest total wins.' },
]

const TEAM_FORMATS = [
  { key: 'individual', label: 'Individual', desc: 'Every player for themselves' },
  { key: 'head_to_head', label: 'Head to Head', desc: '2 players per group — match play, stroke, or Stableford' },
  { key: '2v2_teams', label: '2v2 Teams', desc: 'Pairs compete against each other' },
  { key: 'all_4', label: 'All 4 (Same Team)', desc: 'All 4 players work together' },
]

export default function GroupCompete({ groupId, session, members, isMember }) {
  const [competitions, setCompetitions] = useState([])
  const [tileEntries, setTileEntries] = useState({}) // compId → entries w/ profiles
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('list') // 'list' | 'create' | 'detail'
  const [selectedComp, setSelectedComp] = useState(null)

  // Create form state
  const [compName, setCompName] = useState('')
  const [compCourseName, setCompCourseName] = useState('')
  const [teamFormat, setTeamFormat] = useState('individual')
  const [scoringFormat, setScoringFormat] = useState('best_ball')
  const [scoringType, setScoringType] = useState('rounds')
  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [teamAssignments, setTeamAssignments] = useState({})
  const [guestPlayers, setGuestPlayers] = useState([]) // [{ tempId, name }]
  const [creating, setCreating] = useState(false)

  // Detail view
  const [entries, setEntries] = useState([])

  // Edit competition settings
  const [editingComp, setEditingComp] = useState(false)
  const [editCompName, setEditCompName] = useState('')
  const [editScoringType, setEditScoringType] = useState('rounds')
  const [savingCompEdit, setSavingCompEdit] = useState(false)

  // Tournament matchups shown in compete list
  const [tourneyMatchups, setTourneyMatchups] = useState([]) // [{matchup, round, tournament, entries}]
  const [selectedMatchup, setSelectedMatchup] = useState(null) // { matchup, round, tournament }
  const [matchupEntries, setMatchupEntries] = useState([])

  // Multi-player scorecard state
  const [scorecardOpen, setScorecardOpen] = useState(false)
  const [scorecardEntries, setScorecardEntries] = useState([])
  const [scorecardTable, setScorecardTable] = useState('competition_entries')

  useEffect(() => {
    fetchCompetitions()
    fetchTourneyMatchups()
  }, [groupId])

  // Real-time: refresh entries when scores change
  useEffect(() => {
    if (!selectedComp) return
    fetchEntries(selectedComp.id)

    const channel = supabase
      .channel(`compete-${selectedComp.id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public',
        table: 'competition_entries',
        filter: `competition_id=eq.${selectedComp.id}`
      }, () => fetchEntries(selectedComp.id))
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [selectedComp])

  // ── Data fetching ────────────────────────────────────

  async function fetchCompetitions() {
    const { data: comps } = await supabase
      .from('competitions')
      .select('*')
      .eq('group_id', groupId)
      .order('created_at', { ascending: false })
    const compList = comps || []
    setCompetitions(compList)

    // Batch-fetch all entries for tiles (scores + players)
    if (compList.length > 0) {
      const compIds = compList.map(c => c.id)
      const { data: allEntries } = await supabase
        .from('competition_entries')
        .select('id, competition_id, user_id, guest_name, team_name, score, hole_scores')
        .in('competition_id', compIds)

      const entryRows = allEntries || []
      if (entryRows.length > 0) {
        const userIds = [...new Set(entryRows.filter(e => e.user_id).map(e => e.user_id))]
        let profileMap = {}
        if (userIds.length > 0) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('id, username, avatar_emoji, avatar_url')
            .in('id', userIds)
          ;(profileData || []).forEach(p => { profileMap[p.id] = p })
        }
        const byComp = {}
        entryRows.forEach(e => {
          if (!byComp[e.competition_id]) byComp[e.competition_id] = []
          byComp[e.competition_id].push({ ...e, profiles: e.user_id ? (profileMap[e.user_id] || null) : null })
        })
        setTileEntries(byComp)
      }
    }

    setLoading(false)
  }

  async function fetchEntries(compId) {
    const { data: rows } = await supabase
      .from('competition_entries')
      .select('id, competition_id, user_id, guest_name, team_name, score, hole_scores, updated_at')
      .eq('competition_id', compId)
      .order('score', { ascending: true })

    const entryRows = rows || []
    if (entryRows.length > 0) {
      const realUserIds = entryRows.filter(e => e.user_id).map(e => e.user_id)
      let profileMap = {}
      if (realUserIds.length > 0) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, username, avatar_emoji, avatar_url, handicap')
          .in('id', realUserIds)
        ;(profileData || []).forEach(p => { profileMap[p.id] = p })
      }
      setEntries(entryRows.map(e => ({ ...e, profiles: e.user_id ? (profileMap[e.user_id] || null) : null })))
    } else {
      setEntries([])
    }
  }

  async function fetchTourneyMatchups() {
    // Get all tournaments for this group
    const { data: tournaments } = await supabase
      .from('tournaments')
      .select('id, name, status')
      .eq('group_id', groupId)
    if (!tournaments?.length) return

    const tIds = tournaments.map(t => t.id)
    // Get all rounds
    const { data: rounds } = await supabase
      .from('tournament_rounds')
      .select('*')
      .in('tournament_id', tIds)
      .order('round_number')
    if (!rounds?.length) return

    const roundIds = rounds.map(r => r.id)
    // Get all matchups
    const { data: matchups } = await supabase
      .from('tournament_matchups')
      .select('*')
      .in('round_id', roundIds)
      .order('created_at')
    if (!matchups?.length) return

    const matchupIds = matchups.map(m => m.id)
    // Get all entries for those matchups
    const { data: allEntries } = await supabase
      .from('tournament_matchup_entries')
      .select('*')
      .in('matchup_id', matchupIds)

    // Resolve profiles
    const userIds = [...new Set((allEntries || []).filter(e => e.user_id).map(e => e.user_id))]
    let profileMap = {}
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles').select('id, username, avatar_emoji, avatar_url, handicap').in('id', userIds)
      ;(profiles || []).forEach(p => { profileMap[p.id] = p })
    }

    // Build enriched matchup objects
    const tMap = {}
    tournaments.forEach(t => { tMap[t.id] = t })
    const rMap = {}
    rounds.forEach(r => { rMap[r.id] = r })

    const enriched = matchups.map(mu => {
      const entries = (allEntries || [])
        .filter(e => e.matchup_id === mu.id)
        .map(e => ({ ...e, profiles: e.user_id ? (profileMap[e.user_id] || null) : null }))
      const round = rMap[mu.round_id]
      const tournament = tMap[round?.tournament_id]
      return { matchup: mu, round, tournament, entries }
    })

    setTourneyMatchups(enriched)
  }

  async function fetchMatchupEntries(matchupId) {
    const { data: rows } = await supabase
      .from('tournament_matchup_entries')
      .select('*')
      .eq('matchup_id', matchupId)
    const userIds = [...new Set((rows || []).filter(e => e.user_id).map(e => e.user_id))]
    let profileMap = {}
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles').select('id, username, avatar_emoji, avatar_url, handicap').in('id', userIds)
      ;(profiles || []).forEach(p => { profileMap[p.id] = p })
    }
    setMatchupEntries((rows || []).map(e => ({ ...e, profiles: e.user_id ? (profileMap[e.user_id] || null) : null })))
  }

  // ── Create competition ───────────────────────────────

  const totalPlayers = selectedPlayers.length + guestPlayers.length

  async function handleCreate() {
    if (!compName.trim() || totalPlayers < 2) return
    setCreating(true)

    const { data: comp, error } = await supabase
      .from('competitions')
      .insert({
        group_id: groupId,
        name: compName.trim(),
        course_name: compCourseName.trim() || null,
        format: teamFormat,
        team_format: teamFormat,
        scoring_format: scoringFormat,
        scoring_type: scoringType,
        status: 'active',
        created_by: session.user.id
      })
      .select()
      .single()

    if (error || !comp) { setCreating(false); return }

    const memberEntries = selectedPlayers.map(uid => ({
      competition_id: comp.id,
      user_id: uid,
      guest_name: null,
      team_name: teamAssignments[uid] || null,
      score: 0,
      hole_scores: []
    }))
    const guestEntries = guestPlayers.map(g => ({
      competition_id: comp.id,
      user_id: null,
      guest_name: g.name.trim() || 'Guest',
      team_name: teamAssignments[g.tempId] || null,
      score: 0,
      hole_scores: []
    }))
    await supabase.from('competition_entries').insert([...memberEntries, ...guestEntries])

    setCompName(''); setCompCourseName(''); setTeamFormat('individual'); setScoringFormat('best_ball'); setScoringType('rounds')
    setSelectedPlayers([]); setTeamAssignments({})
    setGuestPlayers([]); setCreating(false)
    await fetchCompetitions()
    openComp(comp)
  }

  function openComp(comp) {
    setSelectedComp(comp)
    setView('detail')
  }

  function openMatchup(item) {
    setSelectedMatchup(item)
    setMatchupEntries(item.entries)
    setView('matchupDetail')
  }

  // ── Guest helpers ────────────────────────────────────

  function addGuest() {
    const tempId = `guest-${Date.now()}-${Math.random()}`
    setGuestPlayers(prev => [...prev, { tempId, name: '' }])
  }
  function removeGuest(tempId) {
    setGuestPlayers(prev => prev.filter(g => g.tempId !== tempId))
    setTeamAssignments(prev => { const n = { ...prev }; delete n[tempId]; return n })
  }
  function updateGuestName(tempId, name) {
    setGuestPlayers(prev => prev.map(g => g.tempId === tempId ? { ...g, name } : g))
  }

  // ── Multi-player scorecard ───────────────────────────

  function openMultiScorecard(entriesList, table) {
    setScorecardEntries(entriesList)
    setScorecardTable(table)
    setScorecardOpen(true)
  }

  async function handleSaveMulti(results) {
    const table = scorecardTable
    for (const r of results) {
      const update = { score: r.score, hole_scores: r.hole_scores }
      if (table === 'competition_entries') update.updated_at = new Date().toISOString()
      await supabase.from(table).update(update).eq('id', r.id)
    }
    setScorecardOpen(false)
    if (selectedComp) fetchEntries(selectedComp.id)
    if (selectedMatchup) {
      await fetchMatchupEntries(selectedMatchup.matchup.id)
      fetchTourneyMatchups()
    }
  }

  async function handleFinishComp() {
    if (!window.confirm('Mark this competition as finished?')) return
    await supabase.from('competitions').update({ status: 'finished' }).eq('id', selectedComp.id)
    setSelectedComp(prev => ({ ...prev, status: 'finished' }))
    fetchCompetitions()
  }

  function openEditComp() {
    setEditCompName(selectedComp.name)
    setEditScoringType(selectedComp.scoring_type)
    setEditingComp(true)
  }

  async function handleSaveCompEdit() {
    if (savingCompEdit) return
    setSavingCompEdit(true)
    const updates = {
      name: editCompName.trim() || selectedComp.name,
      scoring_type: editScoringType
    }
    await supabase.from('competitions').update(updates).eq('id', selectedComp.id)
    setSelectedComp(prev => ({ ...prev, ...updates }))
    fetchCompetitions()
    setSavingCompEdit(false)
    setEditingComp(false)
  }

  // ── Helpers ──────────────────────────────────────────

  function getProgress(entry) {
    const hs = entry.hole_scores
    if (!hs || !Array.isArray(hs)) return null
    const filled = hs.filter(h => h != null)
    if (filled.length === 0) return null
    return {
      holesPlayed: filled.length,
      currentHole: Math.min(filled.length + 1, 18),
      isComplete: filled.length >= 18,
      total: filled.reduce((s, h) => s + h, 0)
    }
  }

  function playerDisplayName(entry) {
    if (entry.user_id) return `@${entry.profiles?.username || 'member'}`
    return entry.guest_name || 'Guest'
  }

  // ── RENDER: List view ────────────────────────────────

  if (view === 'list') return (
    <div style={s.wrapper}>
      <div style={s.listHeader}>
        <div style={s.listTitle}>Competitions</div>
        {isMember && <button style={s.createBtn} onClick={() => setView('create')}>＋ New</button>}
      </div>

      {loading && <div style={s.empty}>Loading...</div>}

      {!loading && competitions.length === 0 && (
        <div style={s.emptyState}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🏆</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1f13', marginBottom: 6 }}>No competitions yet</div>
          <div style={{ fontSize: 13, color: '#6b7c6e' }}>Create one to start competing with your group.</div>
        </div>
      )}

      {/* ── Tournament Matchups section ── */}
      {tourneyMatchups.length > 0 && (
        <div style={s.matchupSection}>
          <div style={s.matchupSectionTitle}>TOURNAMENT MATCHUPS</div>
          <div style={s.tileGrid}>
            {tourneyMatchups.map(({ matchup, round, tournament, entries: mEntries }) => {
              const sorted = [...mEntries].sort((a, b) => (a.position || 99) - (b.position || 99))
              const rankIcons = ['🥇', '🥈', '🥉', '🏅']
              return (
                <div key={matchup.id} style={s.tile} onClick={() => openMatchup({ matchup, round, tournament, entries: mEntries })}>
                  <div style={{
                    ...s.tileBanner,
                    background: matchup.status === 'finished'
                      ? 'linear-gradient(135deg,#2a2a2a,#444)'
                      : 'linear-gradient(135deg,#0e1f13,#1a5e30)'
                  }}>
                    <div style={s.tileStatus}>
                      {matchup.status === 'finished'
                        ? <span style={{ opacity: 0.7 }}>FINAL</span>
                        : <><span style={s.liveDot} />LIVE</>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={s.tileFormat}>{tournament?.name || 'Tournament'}</div>
                      <div style={s.tileDate}>
                        {round?.scheduled_date
                          ? new Date(round.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : new Date(matchup.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                  <div style={s.tileBody}>
                    <div style={s.tileName}>{matchup.name}</div>
                    <div style={s.tileMeta}>
                      {round?.name || ''}
                      {round?.scoring_format
                        ? ` · ${SCORING_FORMATS.find(f => f.key === round.scoring_format)?.label || round.scoring_format}`
                        : round?.team_format
                          ? ` · ${TEAM_FORMATS.find(f => f.key === round.team_format)?.label || round.team_format}`
                          : ''}
                    </div>
                  </div>
                  {sorted.length > 0 && (
                    <div style={s.tileScoreboard}>
                      {sorted.map((entry, i) => (
                        <div key={entry.id} style={{
                          ...s.tileScoreRow,
                          background: i === 0 && entry.position ? 'rgba(200,241,53,0.08)' : 'transparent',
                          borderLeft: i === 0 && entry.position ? '2px solid #c8f135' : '2px solid transparent'
                        }}>
                          <div style={s.tileRank}>
                            {entry.position
                              ? (rankIcons[entry.position - 1] || <span style={s.tileRankNum}>{entry.position}</span>)
                              : <span style={{ color: '#c8d5ca', fontSize: 11 }}>—</span>}
                          </div>
                          <div style={s.tilePlayerAvatar}>
                            {entry.user_id ? <AvatarImg profile={entry.profiles} /> : '👤'}
                          </div>
                          <div style={s.tilePlayerName}>
                            {entry.user_id ? `@${entry.profiles?.username || 'player'}` : (entry.guest_name || 'Guest')}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            {entry.score != null && <div style={s.tileScore}>{entry.score}</div>}
                            {entry.points_earned > 0 && <div style={s.tileThru}>+{entry.points_earned}pts</div>}
                            {(() => { const p = getProgress(entry); return p && !p.isComplete ? <div style={s.tileHole}>● Hole {p.currentHole}</div> : null })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={s.tileArrow}>›</div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {competitions.length > 0 && (
        <div style={s.matchupSectionTitle} >REGULAR MATCHUPS</div>
      )}

      <div style={s.tileGrid}>
        {competitions.map(comp => {
          const compEntries = tileEntries[comp.id] || []
          const sorted = [...compEntries].sort((a, b) =>
            comp.scoring_type === 'rounds' ? a.score - b.score : b.score - a.score
          )
          const dateStr = new Date(comp.created_at).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
          })
          return (
            <div key={comp.id} style={s.tile} onClick={() => openComp(comp)}>
              <div style={{
                ...s.tileBanner,
                background: comp.status === 'active'
                  ? 'linear-gradient(135deg,#0e1f13,#1a7a3c)'
                  : 'linear-gradient(135deg,#2a2a2a,#444)'
              }}>
                <div style={s.tileStatus}>
                  {comp.status === 'active'
                    ? <><span style={s.liveDot} />LIVE</>
                    : <span style={{ opacity: 0.7 }}>FINAL</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={s.tileFormat}>{comp.format}</div>
                  <div style={s.tileDate}>{dateStr}</div>
                </div>
              </div>
              <div style={s.tileBody}>
                <div style={s.tileName}>{comp.name}</div>
                <div style={s.tileMeta}>
                  {comp.scoring_format
                    ? [
                        TEAM_FORMATS.find(f => f.key === comp.team_format)?.label,
                        SCORING_FORMATS.find(f => f.key === comp.scoring_format)?.label
                      ].filter(Boolean).join(' · ')
                    : comp.scoring_type === 'rounds' ? '⛳ Golf Score' : '🎯 Custom Points'}
                </div>
              </div>
              {sorted.length > 0 && (
                <div style={s.tileScoreboard}>
                  {sorted.map((entry, i) => {
                    const profile = entry.profiles || {}
                    const medals = ['🥇', '🥈', '🥉']
                    const prog = getProgress(entry)
                    return (
                      <div key={entry.id} style={{
                        ...s.tileScoreRow,
                        background: i === 0 ? 'rgba(200,241,53,0.08)' : 'transparent',
                        borderLeft: i === 0 ? '2px solid #c8f135' : '2px solid transparent'
                      }}>
                        <div style={s.tileRank}>
                          {i < 3 ? medals[i] : <span style={s.tileRankNum}>{i + 1}</span>}
                        </div>
                        <div style={s.tilePlayerAvatar}>
                          {entry.user_id ? <AvatarImg profile={profile} /> : '👤'}
                        </div>
                        <div style={s.tilePlayerName}>
                          {entry.user_id ? `@${profile.username || 'player'}` : (entry.guest_name || 'Guest')}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={s.tileScore}>{entry.score}</div>
                          {prog && !prog.isComplete && (
                            <div style={s.tileHole}>● Hole {prog.currentHole}</div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              <div style={s.tileArrow}>›</div>
            </div>
          )
        })}
      </div>
    </div>
  )

  // ── RENDER: Create view ──────────────────────────────

  if (view === 'create') return (
    <div style={s.wrapper}>
      <div style={s.createHeader}>
        <button style={s.backBtn} onClick={() => setView('list')}>← Back</button>
        <div style={s.createTitle}>New Competition</div>
        <div style={{ width: 60 }} />
      </div>
      <div style={s.createBody}>

        <div style={s.field}>
          <label style={s.label}>Competition Name</label>
          <input style={s.input} placeholder="e.g. Saturday Skins Game"
            value={compName} onChange={e => setCompName(e.target.value)} maxLength={60} />
        </div>

        <div style={s.field}>
          <label style={s.label}>Course (optional)</label>
          <input style={s.input} placeholder="e.g. Pebble Beach"
            value={compCourseName} onChange={e => setCompCourseName(e.target.value)} maxLength={80} />
        </div>

        <div style={s.field}>
          <label style={s.label}>Team Format</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {TEAM_FORMATS.map(f => (
              <div key={f.key} style={{ ...s.formatCard, ...(teamFormat === f.key ? s.formatCardActive : {}) }}
                onClick={() => setTeamFormat(f.key)}>
                <div style={{ flex: 1 }}>
                  <div style={s.formatCardTitle}>{f.label}</div>
                  <div style={s.formatCardDesc}>{f.desc}</div>
                </div>
                {teamFormat === f.key && <div style={s.formatDot} />}
              </div>
            ))}
          </div>
        </div>

        <div style={s.field}>
          <label style={s.label}>Scoring Format</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SCORING_FORMATS.map(f => (
              <div key={f.key} style={{ ...s.formatCard, ...(scoringFormat === f.key ? s.formatCardActive : {}) }}
                onClick={() => setScoringFormat(f.key)}>
                <div style={{ flex: 1 }}>
                  <div style={s.formatCardTitle}>{f.label}</div>
                  <div style={s.formatCardDesc}>{f.desc}</div>
                </div>
                {scoringFormat === f.key && <div style={s.formatDot} />}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {SCORING_TYPES.map(st => (
              <button key={st.key}
                style={{ ...s.scoringTypeBtn, ...(scoringType === st.key ? s.scoringTypeBtnActive : {}) }}
                onClick={() => setScoringType(st.key)}>
                <span>{st.label}</span>
                <span style={{ fontSize: 9, opacity: 0.65, marginLeft: 4 }}>({st.hint})</span>
              </button>
            ))}
          </div>
        </div>

        <div style={s.field}>
          <label style={s.label}>Players <span style={{ color: '#6b7c6e', fontWeight: 400 }}>(select at least 2)</span></label>
          {members.map(m => {
            const profile = m.profiles || {}
            const uid = m.user_id
            const selected = selectedPlayers.includes(uid)
            return (
              <div key={uid} style={s.playerRow}>
                <div
                  style={{ ...s.playerCheck, background: selected ? '#0e1f13' : 'white', borderColor: selected ? '#0e1f13' : '#e2ebe4' }}
                  onClick={() => setSelectedPlayers(prev =>
                    prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
                  )}>
                  {selected && <span style={{ color: '#c8f135', fontSize: 12 }}>✓</span>}
                </div>
                <div style={s.playerAvatar}><AvatarImg profile={profile} /></div>
                <div style={s.playerInfo}>
                  <div style={s.playerName}>@{profile.username || 'member'}</div>
                  {profile.handicap != null && <div style={s.playerHcp}>HCP {profile.handicap}</div>}
                </div>
                {selected && (teamFormat === '2v2_teams') && (
                  <input style={s.teamInput} placeholder="Team name"
                    value={teamAssignments[uid] || ''}
                    onChange={e => setTeamAssignments(prev => ({ ...prev, [uid]: e.target.value }))}
                    maxLength={20} />
                )}
              </div>
            )
          })}

          {guestPlayers.length > 0 && (
            <div style={s.guestDivider}><span style={s.guestDividerLabel}>Guests</span></div>
          )}
          {guestPlayers.map(g => (
            <div key={g.tempId} style={s.guestRow}>
              <div style={s.guestAvatar}>👤</div>
              <input style={s.guestNameInput} placeholder="Guest name"
                value={g.name} onChange={e => updateGuestName(g.tempId, e.target.value)}
                maxLength={40} autoFocus={g.name === ''} />
              {teamFormat === '2v2_teams' && (
                <input style={s.teamInput} placeholder="Team"
                  value={teamAssignments[g.tempId] || ''}
                  onChange={e => setTeamAssignments(prev => ({ ...prev, [g.tempId]: e.target.value }))}
                  maxLength={20} />
              )}
              <button style={s.guestRemoveBtn} onClick={() => removeGuest(g.tempId)}>✕</button>
            </div>
          ))}
          <button style={s.addGuestBtn} onClick={addGuest}>＋ Add Guest Player</button>
        </div>

        <button
          style={{ ...s.submitBtn, opacity: compName.trim() && totalPlayers >= 2 && !creating ? 1 : 0.4 }}
          onClick={handleCreate}
          disabled={!compName.trim() || totalPlayers < 2 || creating}>
          {creating ? 'Creating...' : '🏆 Start Competition'}
        </button>
      </div>
    </div>
  )

  // ── RENDER: Detail view ──────────────────────────────

  if (view === 'detail' && selectedComp) {
    const isActive = selectedComp.status === 'active'
    const canEdit = isMember || session.user.id === selectedComp.created_by

    const sorted = [...entries].sort((a, b) =>
      selectedComp.scoring_type === 'rounds' ? a.score - b.score : b.score - a.score
    )

    const isTeamFormat = selectedComp.format === '2v2' || selectedComp.format === 'Teams'
    const teamScores = {}
    if (isTeamFormat) {
      entries.forEach(e => {
        const team = e.team_name || 'No Team'
        if (!teamScores[team]) teamScores[team] = 0
        teamScores[team] += e.score
      })
    }

    const medals = ['🥇', '🥈', '🥉']

    return (
      <div style={s.wrapper}>

        {/* ── Multi-player scorecard overlay ── */}
        {scorecardOpen && (
          <ScorecardOverlay
            entries={scorecardEntries}
            onSave={handleSaveMulti}
            onClose={() => setScorecardOpen(false)}
            title={selectedComp.name}
          />
        )}

        {/* ── Edit Competition Settings Modal ── */}
        {editingComp && (
          <div style={s.scOverlay}>
            <div style={{ ...s.scModal, maxHeight: '60vh' }}>
              <div style={s.scHeader}>
                <button style={s.scCancelBtn} onClick={() => setEditingComp(false)}>Cancel</button>
                <div style={s.scHeaderCenter}>
                  <div style={s.scPlayerName}>Edit Competition</div>
                </div>
                <button style={{ ...s.scSaveBtn, opacity: savingCompEdit ? 0.5 : 1 }} onClick={handleSaveCompEdit} disabled={savingCompEdit}>
                  {savingCompEdit ? '...' : 'Save'}
                </button>
              </div>
              <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <div style={s.editFieldLabel}>Competition Name</div>
                  <input style={s.scTotalModeInput} value={editCompName} onChange={e => setEditCompName(e.target.value)} maxLength={60} />
                </div>
                <div>
                  <div style={s.editFieldLabel}>Scoring Type</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                    {SCORING_TYPES.map(st => (
                      <button key={st.key}
                        style={{ ...s.scoringBtn, ...(editScoringType === st.key ? s.scoringActive : {}) }}
                        onClick={() => setEditScoringType(st.key)}>
                        <div style={s.scoringBtnLabel}>{st.label}</div>
                        <div style={s.scoringBtnDesc}>{st.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ESPN-style competition header ── */}
        <div style={s.detailBanner}>
          <button style={s.backBtnLight}
            onClick={() => { setView('list'); setSelectedComp(null); setEntries([]) }}>
            ← Back
          </button>
          <div style={s.detailBannerCenter}>
            <div style={s.detailBannerTitle}>{selectedComp.name}</div>
            <div style={s.detailBannerMeta}>
              {selectedComp.format} · {selectedComp.scoring_type === 'rounds' ? 'Golf Score' : 'Custom Points'}
              {' · '}{new Date(selectedComp.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            {selectedComp.course_name && (
              <div style={{ ...s.detailBannerMeta, marginTop: 2 }}>⛳ {selectedComp.course_name}</div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <div style={{
              ...s.detailStatusBadge,
              background: isActive ? '#c8f135' : 'rgba(255,255,255,0.15)',
              color: isActive ? '#0e1f13' : 'rgba(255,255,255,0.6)'
            }}>
              {isActive ? '● LIVE' : '✓ DONE'}
            </div>
            {canEdit && (
              <button style={s.editSettingsBtn} onClick={openEditComp}>✎ Edit</button>
            )}
          </div>
        </div>

        {/* ── Team totals bar ── */}
        {isTeamFormat && (
          <div style={s.teamBar}>
            {Object.entries(teamScores)
              .sort(([, a], [, b]) => selectedComp.scoring_type === 'rounds' ? a - b : b - a)
              .map(([team, total], i) => (
                <div key={team} style={{ ...s.teamChip, borderColor: i === 0 ? '#c8f135' : '#e2ebe4' }}>
                  {i === 0 && <div style={s.teamLeadBadge}>LEADS</div>}
                  <div style={s.teamChipName}>{team}</div>
                  <div style={s.teamChipTotal}>{total}</div>
                </div>
              ))}
          </div>
        )}

        {/* ── ESPN standings table ── */}
        <div style={s.espnTable}>
          <div style={s.espnHeader}>
            <div style={s.espnColRank}>#</div>
            <div style={s.espnColPlayer}>PLAYER</div>
            <div style={s.espnColScore}>
              SCORE
              <div style={{ fontSize: 9, color: '#9aaa9d', fontWeight: 400, letterSpacing: 0, textTransform: 'none', marginTop: 1 }}>
                {selectedComp.scoring_type === 'rounds' ? 'low wins' : 'high wins'}
              </div>
            </div>
          </div>

          {sorted.map((entry, i) => {
            const profile = entry.profiles || {}
            const isLeader = i === 0
            const prog = getProgress(entry)

            return (
              <div key={entry.id} style={{
                ...s.espnRow,
                background: isLeader ? 'rgba(200,241,53,0.07)' : i % 2 === 0 ? 'white' : '#fafcfb',
                borderLeft: isLeader ? '3px solid #c8f135' : '3px solid transparent'
              }}>
                {/* Rank */}
                <div style={s.espnColRank}>
                  {i < 3
                    ? <span style={{ fontSize: 18 }}>{medals[i]}</span>
                    : <span style={s.rankNum}>{i + 1}</span>}
                </div>

                {/* Player */}
                <div style={s.espnColPlayer}>
                  <div style={s.espnAvatar}>
                    {entry.user_id ? <AvatarImg profile={profile} /> : '👤'}
                  </div>
                  <div>
                    <div style={s.espnName}>
                      {entry.user_id ? `@${profile.username || 'member'}` : (entry.guest_name || 'Guest')}
                    </div>
                    {!entry.user_id && <div style={s.espnGuestTag}>Guest</div>}
                    {entry.team_name && <div style={s.espnTeamTag}>{entry.team_name}</div>}
                    {entry.user_id && profile.handicap != null && (
                      <div style={s.espnHcp}>HCP {profile.handicap}</div>
                    )}
                  </div>
                </div>

                {/* Score — tap to open multi-player scorecard */}
                <div style={s.espnColScore}>
                  <div
                    style={{ ...s.espnScore, cursor: canEdit ? 'pointer' : 'default' }}
                    onClick={() => canEdit && openMultiScorecard(sorted, 'competition_entries')}
                    title={canEdit ? 'Tap to enter scores' : ''}
                  >
                    <div style={s.espnScoreNum}>{entry.score}</div>
                    {/* Progress indicator if hole-by-hole in progress */}
                    {prog && !prog.isComplete && (
                      <div style={s.espnThru}>● Hole {prog.currentHole}</div>
                    )}
                    {prog && prog.isComplete && (
                      <div style={s.espnComplete}>✓ F</div>
                    )}
                    {canEdit && !prog && <div style={s.espnEditHint}>✎</div>}
                    {canEdit && prog && <div style={s.espnEditHint}>✎</div>}
                  </div>
                </div>
              </div>
            )
          })}

          {entries.length === 0 && (
            <div style={s.empty}>No players in this competition yet.</div>
          )}
        </div>

        {/* Enter Scores button */}
        {canEdit && entries.length > 0 && isActive && (
          <div style={{ padding: '12px 16px 0' }}>
            <button style={s.enterScoresBtn} onClick={() => openMultiScorecard(sorted, 'competition_entries')}>
              ⛳ Enter Scores
            </button>
          </div>
        )}

        {/* Finish button */}
        {isActive && isMember && (
          <div style={{ padding: '16px' }}>
            <button style={s.finishBtn} onClick={handleFinishComp}>Mark as Finished</button>
          </div>
        )}

      </div>
    )
  }

  // ── RENDER: Matchup detail view ──────────────────────

  if (view === 'matchupDetail' && selectedMatchup) {
    const { matchup, round, tournament } = selectedMatchup
    const isCreator = session.user.id === tournament?.created_by
    const myEntry = matchupEntries.find(e => e.user_id === session.user.id)
    const canEdit = isCreator || !!myEntry
    const sorted = [...matchupEntries].sort((a, b) => (a.position || 99) - (b.position || 99))
    const rankIcons = ['🥇', '🥈', '🥉', '🏅']

    return (
      <div style={s.wrapper}>

        {/* Multi-player scorecard overlay */}
        {scorecardOpen && (
          <ScorecardOverlay
            entries={scorecardEntries}
            onSave={handleSaveMulti}
            onClose={() => setScorecardOpen(false)}
            title={matchup.name}
          />
        )}

        {/* Header */}
        <div style={s.detailBanner}>
          <button style={s.backBtnLight} onClick={() => { setView('list'); setSelectedMatchup(null); setMatchupEntries([]) }}>← Back</button>
          <div style={s.detailBannerCenter}>
            <div style={s.detailBannerTitle}>{matchup.name}</div>
            <div style={s.detailBannerMeta}>
              {tournament?.name} · {round?.name}
              {round?.scheduled_date ? ` · ${new Date(round.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
            </div>
          </div>
          <div style={{
            ...s.detailStatusBadge,
            background: matchup.status === 'finished' ? 'rgba(255,255,255,0.15)' : '#c8f135',
            color: matchup.status === 'finished' ? 'rgba(255,255,255,0.6)' : '#0e1f13'
          }}>
            {matchup.status === 'finished' ? '✓ DONE' : '● LIVE'}
          </div>
        </div>

        {/* Points banner */}
        <div style={{ display: 'flex', background: '#0e1f13', padding: '10px 0' }}>
          {['1st','2nd','3rd','4th'].map((pl, pi) => {
            const fields = ['points_1st','points_2nd','points_3rd','points_4th']
            return (
              <div key={pl} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 20, color: '#c8f135', lineHeight: 1 }}>
                  {round?.[fields[pi]] ?? '—'}
                </div>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 3 }}>{pl}</div>
              </div>
            )
          })}
        </div>

        {/* Standings table */}
        <div style={s.espnTable}>
          <div style={s.espnHeader}>
            <div style={s.espnColRank}>#</div>
            <div style={s.espnColPlayer}>PLAYER</div>
            <div style={s.espnColScore}>SCORE</div>
          </div>

          {sorted.map((entry, i) => {
            const isMe = entry.user_id === session.user.id
            const canEditEntry = isCreator || isMe
            const prog = getProgress(entry)
            return (
              <div key={entry.id} style={{
                ...s.espnRow,
                background: i === 0 && entry.position ? 'rgba(200,241,53,0.07)' : i % 2 === 0 ? 'white' : '#fafcfb',
                borderLeft: i === 0 && entry.position ? '3px solid #c8f135' : '3px solid transparent'
              }}>
                <div style={s.espnColRank}>
                  {entry.position
                    ? (i < 3 ? <span style={{ fontSize: 18 }}>{rankIcons[i]}</span> : <span style={s.rankNum}>{entry.position}</span>)
                    : <span style={{ color: '#c8d5ca' }}>—</span>}
                </div>
                <div style={s.espnColPlayer}>
                  <div style={s.espnAvatar}>{entry.user_id ? <AvatarImg profile={entry.profiles} /> : '👤'}</div>
                  <div>
                    <div style={s.espnName}>
                      {entry.user_id ? `@${entry.profiles?.username || 'member'}` : (entry.guest_name || 'Guest')}
                      {isMe && <span style={{ color: '#1a7a3c', fontSize: 10, marginLeft: 4 }}>you</span>}
                    </div>
                    {!entry.user_id && <div style={s.espnGuestTag}>Guest</div>}
                    {entry.points_earned > 0 && (
                      <div style={{ fontSize: 10, color: '#1a7a3c', fontWeight: 700 }}>+{entry.points_earned} pts</div>
                    )}
                  </div>
                </div>
                <div style={s.espnColScore}>
                  <div
                    style={{ ...s.espnScore, cursor: canEditEntry ? 'pointer' : 'default' }}
                    onClick={() => canEditEntry && openMultiScorecard(matchupEntries, 'tournament_matchup_entries')}
                  >
                    <div style={s.espnScoreNum}>{entry.score != null ? entry.score : '—'}</div>
                    {prog && !prog.isComplete && <div style={s.espnThru}>● Hole {prog.currentHole}</div>}
                    {prog && prog.isComplete && <div style={s.espnComplete}>✓ F</div>}
                    {canEditEntry && <div style={s.espnEditHint}>✎</div>}
                  </div>
                </div>
              </div>
            )
          })}

          {matchupEntries.length === 0 && <div style={s.empty}>No players in this matchup.</div>}
        </div>

        {canEdit && matchupEntries.length > 0 && (
          <div style={{ padding: '12px 16px 0' }}>
            <button style={s.enterScoresBtn} onClick={() => openMultiScorecard(matchupEntries, 'tournament_matchup_entries')}>
              ⛳ Enter Scores
            </button>
          </div>
        )}
      </div>
    )
  }

  return null
}

// ── STYLES ───────────────────────────────────────────────────
const s = {
  wrapper: { background: '#f4f7f5', flex: 1, overflowY: 'auto', minHeight: 0 },

  // ── Tournament matchup section ────────────────────────
  matchupSection: { marginBottom: 4 },
  matchupSectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 11, color: '#0e1f13', letterSpacing: 2,
    padding: '14px 16px 8px', background: 'white',
    borderBottom: '1px solid #f0f4f1'
  },

  // ── List ─────────────────────────────────────────────
  listHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 16px 12px', background: 'white', borderBottom: '1px solid #f0f4f1'
  },
  listTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 18, color: '#0e1f13', letterSpacing: 0.5
  },
  createBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 8, padding: '7px 14px', fontWeight: 800,
    fontSize: 13, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif"
  },
  empty: { padding: 24, textAlign: 'center', color: '#6b7c6e', fontSize: 13 },
  emptyState: { padding: '40px 20px', textAlign: 'center' },

  // Tile grid
  tileGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 12, padding: 16
  },
  tile: {
    background: 'white', borderRadius: 16, overflow: 'hidden', cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column',
    border: '1px solid rgba(0,0,0,0.04)'
  },
  tileBanner: {
    padding: '14px 16px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  tileStatus: {
    color: 'white', fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
    textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 5,
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  liveDot: {
    display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#c8f135'
  },
  tileFormat: {
    background: 'rgba(255,255,255,0.15)', color: 'white', borderRadius: 20,
    padding: '3px 10px', fontSize: 11, fontWeight: 700,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  tileDate: { color: 'rgba(255,255,255,0.5)', fontSize: 10, fontFamily: "'Barlow', sans-serif", fontWeight: 500 },
  tileBody: { padding: '12px 16px 8px' },
  tileName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 18, color: '#0e1f13', marginBottom: 4
  },
  tileMeta: { fontSize: 12, color: '#6b7c6e' },
  tileScoreboard: { borderTop: '1px solid #f0f4f1', margin: '8px 0 0' },
  tileScoreRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 14px', borderBottom: '1px solid #f8faf8'
  },
  tileRank: { width: 20, textAlign: 'center', fontSize: 14, flexShrink: 0 },
  tileRankNum: {
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 12, color: '#6b7c6e'
  },
  tilePlayerAvatar: {
    fontSize: 14, width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  tilePlayerName: {
    flex: 1, fontSize: 12, fontWeight: 700, color: '#0e1f13',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  tileScore: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 18, color: '#0e1f13', textAlign: 'right'
  },
  tileThru: { fontSize: 9, color: '#9aaa9d', textAlign: 'right', marginTop: 1 },
  tileHole: { fontSize: 9, color: '#2aad56', fontWeight: 700, textAlign: 'right', marginTop: 1 },
  tileArrow: {
    fontSize: 20, color: '#c8d5ca', fontWeight: 300, padding: '8px 16px 14px', textAlign: 'right'
  },

  // ── Create ───────────────────────────────────────────
  createHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', background: 'white', borderBottom: '1px solid #f0f4f1'
  },
  backBtn: {
    background: 'none', border: 'none', color: '#1a7a3c',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", padding: 0
  },
  createTitle: {
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 16, color: '#0e1f13'
  },
  createBody: { padding: '16px', display: 'flex', flexDirection: 'column', gap: 20 },
  field: {},
  label: {
    display: 'block', fontSize: 10, fontWeight: 700,
    letterSpacing: 1.5, textTransform: 'uppercase', color: '#6b7c6e', marginBottom: 8
  },
  input: {
    width: '100%', background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 10, padding: '12px 14px', fontSize: 14,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box'
  },
  optionGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  optionBtn: {
    background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 10, padding: '9px 16px', fontSize: 13,
    fontWeight: 700, color: '#6b7c6e', cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  optionActive: { background: '#e8f5ed', borderColor: '#2aad56', color: '#1a7a3c' },
  scoringBtn: {
    width: '100%', background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 12, padding: '12px 14px', cursor: 'pointer',
    textAlign: 'left', fontFamily: "'Barlow', sans-serif"
  },
  scoringActive: { background: '#e8f5ed', borderColor: '#2aad56' },
  scoringBtnLabel: { fontWeight: 700, fontSize: 14, color: '#0e1f13', marginBottom: 2 },
  scoringBtnDesc: { fontSize: 11, color: '#6b7c6e' },
  formatCard: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 14, padding: '14px 16px', cursor: 'pointer'
  },
  formatCardActive: { border: '2px solid #1a7a3c', background: '#f7fdf9' },
  formatCardTitle: { fontWeight: 700, fontSize: 14, color: '#0e1f13', marginBottom: 3, fontFamily: "'Barlow', sans-serif" },
  formatCardDesc: { fontSize: 12, color: '#6b7c6e', lineHeight: 1.4 },
  formatDot: { width: 10, height: 10, borderRadius: '50%', background: '#1a7a3c', flexShrink: 0 },
  scoringTypeBtn: {
    flex: 1, background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 10, padding: '9px 10px', fontSize: 12, fontWeight: 700, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif", textAlign: 'center'
  },
  scoringTypeBtnActive: { background: '#e8f5ed', borderColor: '#2aad56', color: '#1a7a3c' },
  playerRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 0', borderBottom: '1px solid #f0f4f1'
  },
  playerCheck: {
    width: 22, height: 22, borderRadius: 6, border: '1.5px solid #e2ebe4',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0
  },
  playerAvatar: {
    width: 34, height: 34, borderRadius: '50%', fontSize: 18, flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  playerInfo: { flex: 1 },
  playerName: { fontWeight: 700, fontSize: 13, color: '#0e1f13' },
  playerHcp: { fontSize: 11, color: '#6b7c6e' },
  teamInput: {
    width: 90, background: '#f4f7f5', border: '1px solid #e2ebe4',
    borderRadius: 8, padding: '5px 8px', fontSize: 12,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif"
  },
  guestDivider: { display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0 4px' },
  guestDividerLabel: {
    fontSize: 10, fontWeight: 700, color: '#6b7c6e', letterSpacing: 1, textTransform: 'uppercase'
  },
  guestRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f4f1'
  },
  guestAvatar: {
    width: 34, height: 34, borderRadius: '50%', fontSize: 18, flexShrink: 0,
    background: '#f0f4f1', border: '1.5px dashed #c8d5ca',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  guestNameInput: {
    flex: 1, background: '#f4f7f5', border: '1.5px solid #e2ebe4',
    borderRadius: 8, padding: '7px 10px', fontSize: 13,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif", fontWeight: 600
  },
  guestRemoveBtn: {
    background: 'none', border: 'none', color: '#c0392b', fontSize: 14, cursor: 'pointer', padding: '4px 6px', flexShrink: 0
  },
  addGuestBtn: {
    marginTop: 12, background: 'none', border: '1.5px dashed #c8d5ca',
    borderRadius: 10, padding: '10px 0', width: '100%',
    fontSize: 13, fontWeight: 700, color: '#6b7c6e', cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", letterSpacing: 0.3
  },
  submitBtn: {
    width: '100%', background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 14, padding: '16px', fontSize: 16, fontWeight: 900, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },

  // ── Scorecard modal ───────────────────────────────────
  scOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },
  scModal: {
    background: '#f4f7f5', width: '100%', maxWidth: 600, borderRadius: 20,
    maxHeight: '92vh', overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(0,0,0,0.2)'
  },
  scHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 18px 14px', background: '#0e1f13',
    borderRadius: '20px 20px 0 0', position: 'sticky', top: 0, zIndex: 10
  },
  scCancelBtn: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", padding: 0
  },
  scHeaderCenter: { textAlign: 'center' },
  scPlayerName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 18, color: 'white', letterSpacing: 0.5
  },
  scGuestTag: { fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2, fontStyle: 'italic' },
  scSaveBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 8, padding: '7px 16px', fontWeight: 800,
    fontSize: 14, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif"
  },

  // Mode tabs
  scTabs: {
    display: 'flex', background: 'white', borderBottom: '1.5px solid #f0f4f1'
  },
  scTab: {
    flex: 1, background: 'none', border: 'none', padding: '12px 4px',
    fontSize: 13, fontWeight: 700, color: '#6b7c6e', cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", borderBottom: '2.5px solid transparent', marginBottom: -2
  },
  scTabActive: { color: '#0e1f13', borderBottom: '2.5px solid #c8f135' },

  // Scorecard card body
  scCardBody: { padding: '16px' },

  // Running total
  scTotalsBar: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 },
  scTotalPill: {
    background: '#0e1f13', borderRadius: 14, padding: '10px 20px', textAlign: 'center', flexShrink: 0
  },
  scTotalNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 32, color: '#c8f135', lineHeight: 1
  },
  scTotalLabel: { fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 3, textTransform: 'uppercase', letterSpacing: 1 },
  scProgressBar: {
    flex: 1, height: 5, background: '#e2ebe4', borderRadius: 10, overflow: 'hidden'
  },
  scProgressFill: {
    height: '100%', background: '#c8f135', borderRadius: 10,
    transition: 'width 0.3s ease'
  },

  // Front/back 9 wrapper
  scNineWrap: { marginBottom: 14 },
  scNineLabel: {
    fontSize: 9, fontWeight: 800, color: '#6b7c6e', letterSpacing: 2,
    textTransform: 'uppercase', marginBottom: 6
  },
  scGridScroll: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  scGrid: { display: 'inline-flex', flexDirection: 'column', minWidth: '100%' },

  // Header row (hole numbers)
  scHeaderRow: {
    display: 'flex'
  },
  scHoleNum: {
    width: 38, height: 28, flexShrink: 0,
    background: '#0e1f13', color: 'rgba(255,255,255,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 10, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5,
    borderRight: '1px solid rgba(255,255,255,0.08)'
  },
  scOutIn: {
    background: '#061008', color: '#c8f135', letterSpacing: 0.5
  },

  // Score inputs row
  scScoreRow: { display: 'flex' },
  scHoleCell: {
    width: 38, height: 44, flexShrink: 0,
    border: '1px solid #e2ebe4', borderTop: 'none', borderRight: 'none',
    textAlign: 'center', fontSize: 16, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif",
    color: '#0e1f13', outline: 'none',
    // Remove number input arrows
    MozAppearance: 'textfield'
  },
  scSubTotal: {
    width: 38, height: 44, flexShrink: 0,
    background: '#e8f5ed', borderTop: 'none', border: '1px solid #c8e6d0',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 16, color: '#0e1f13'
  },

  // Total row at the bottom
  scTotalRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'white', borderRadius: 10, padding: '12px 16px',
    border: '1px solid #e2ebe4'
  },
  scTotalRowLabel: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 13, color: '#6b7c6e', letterSpacing: 1.5
  },
  scTotalRowNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 28, color: '#0e1f13'
  },

  // Final score mode
  scTotalMode: {
    padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12
  },
  scTotalModeLabel: {
    fontSize: 12, fontWeight: 700, color: '#6b7c6e',
    textTransform: 'uppercase', letterSpacing: 1
  },
  scTotalModeInput: {
    width: 140, background: 'white', border: '2px solid #0e1f13',
    borderRadius: 14, padding: '14px', fontSize: 36, fontWeight: 900,
    color: '#0e1f13', outline: 'none', textAlign: 'center',
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  scTotalModeHint: {
    fontSize: 11, color: '#9aaa9d', textAlign: 'center', lineHeight: 1.4
  },

  // ── Detail banner ─────────────────────────────────────
  detailBanner: {
    background: '#0e1f13', padding: '14px 16px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10
  },
  backBtnLight: {
    background: 'none', border: 'none', color: '#c8f135',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", padding: 0, flexShrink: 0
  },
  detailBannerCenter: { flex: 1, textAlign: 'center' },
  detailBannerTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 18, color: 'white', letterSpacing: 0.5
  },
  detailBannerMeta: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 },
  detailStatusBadge: {
    borderRadius: 20, padding: '4px 10px', fontSize: 10, fontWeight: 800,
    letterSpacing: 1, fontFamily: "'Barlow Condensed', sans-serif", flexShrink: 0
  },

  // ── Team totals ───────────────────────────────────────
  teamBar: {
    display: 'flex', gap: 10, padding: '12px 16px', background: 'white',
    borderBottom: '1px solid #f0f4f1', overflowX: 'auto'
  },
  teamChip: {
    border: '1.5px solid #e2ebe4', borderRadius: 12, padding: '10px 16px',
    textAlign: 'center', minWidth: 88, flexShrink: 0, position: 'relative'
  },
  teamLeadBadge: {
    position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
    background: '#c8f135', color: '#0e1f13', borderRadius: 10,
    fontSize: 8, fontWeight: 800, padding: '2px 7px', letterSpacing: 0.5,
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  teamChipName: { fontSize: 11, fontWeight: 700, color: '#6b7c6e', marginBottom: 4 },
  teamChipTotal: {
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 26, color: '#0e1f13', lineHeight: 1
  },

  // ── ESPN table ────────────────────────────────────────
  espnTable: { background: 'white', margin: '12px 0 0' },
  espnHeader: {
    display: 'grid', gridTemplateColumns: '44px 1fr 90px',
    background: '#0e1f13', padding: '10px 16px',
    fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)',
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  espnRow: {
    display: 'grid', gridTemplateColumns: '44px 1fr 90px',
    padding: '12px 16px', borderBottom: '1px solid #f0f4f1', alignItems: 'center'
  },
  espnColRank: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  espnColPlayer: { display: 'flex', alignItems: 'center', gap: 10 },
  espnColScore: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
  rankNum: {
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 15, color: '#6b7c6e'
  },
  espnAvatar: {
    width: 38, height: 38, borderRadius: '50%', fontSize: 20, flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  espnName: { fontWeight: 700, fontSize: 14, color: '#0e1f13' },
  espnGuestTag: { fontSize: 10, color: '#9aaa9d', marginTop: 1, fontStyle: 'italic' },
  espnTeamTag: { fontSize: 10, color: '#6b7c6e', marginTop: 1 },
  espnHcp: { fontSize: 10, color: '#6b7c6e', marginTop: 1 },
  espnScore: {
    textAlign: 'right', padding: '4px 8px', borderRadius: 10, minWidth: 54
  },
  espnScoreNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 28, color: '#0e1f13', lineHeight: 1
  },
  espnThru: { fontSize: 10, color: '#2aad56', fontWeight: 800, textAlign: 'right', marginTop: 2, letterSpacing: 0.3 },
  espnComplete: { fontSize: 9, color: '#1a7a3c', fontWeight: 700, textAlign: 'right', marginTop: 2 },
  espnEditHint: { fontSize: 9, color: '#9aaa9d', textAlign: 'right', marginTop: 1 },

  editHintBar: {
    padding: '10px 16px', textAlign: 'center', fontSize: 11,
    color: '#9aaa9d', fontStyle: 'italic'
  },
  editSettingsBtn: {
    background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8,
    color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600,
    padding: '4px 10px', cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  editFieldLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
    textTransform: 'uppercase', color: '#6b7c6e', marginBottom: 4
  },
  scoringTypeToggleRow: { marginBottom: 8 },
  scoringTypeBtn: {
    flex: 1, background: '#f4f7f5', border: '1.5px solid #e2ebe4', borderRadius: 8,
    padding: '6px 10px', fontSize: 11, fontWeight: 600, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif", display: 'flex', alignItems: 'center'
  },
  scoringTypeBtnActive: { background: '#e8f5ed', borderColor: '#2aad56', color: '#1a7a3c' },
  finishBtn: {
    width: '100%', background: 'none', border: '1.5px solid #e2ebe4',
    borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 700,
    color: '#6b7c6e', cursor: 'pointer', fontFamily: "'Barlow', sans-serif", marginTop: 4
  },
  enterScoresBtn: {
    width: '100%', background: '#0e1f13', color: '#c8f135', border: 'none',
    borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 800,
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  }
}
